// SEO Keywords and Configuration
export const SEO_CONFIG = {
  siteName: 'Piper Card',
  siteUrl: 'https://pipercard.com',
  defaultTitle: 'Piper Card | Digital Business Cards with Built-in CRM & Sales Pipeline',
  titleTemplate: '%s | Piper Card',
  defaultDescription: 'Create professional digital business cards with QR codes. Includes complete CRM system, custom sales pipelines, team collaboration, and contact management. Powerful features with fresh new identity.',
  keywords: [
    // Primary keywords
    'digital business card',
    'virtual business card',
    'QR code business card',
    'CRM software',
    'sales pipeline management',
    'team collaboration',
    
    // Secondary keywords - CRM Features
    'online CRM',
    'contact management',
    'lead management',
    'sales tracking',
    'custom pipelines',
    'PiperCard',
    
    // Original Secondary keywords
    'contactless networking',
    'digital contact card',
    'QR code contact sharing',
    'mobile business card',
    'electronic business card',
    'smart business card',
    
    // Long-tail keywords - CRM focused
    'digital business card with CRM',
    'CRM with digital business cards',
    'sales pipeline software',
    'team collaboration CRM',
    'create custom sales pipeline',
    'manage sales leads online',
    'business card scanner with CRM',
    
    // Original Long-tail keywords
    'create digital business card free',
    'share business card via QR code',
    'digital business card with QR code',
    'eco friendly business cards',
    'paperless business card',
    'instant contact sharing',
    'professional digital card',
    'business card maker online',
    
    // Feature-specific - CRM Enhanced
    'CRM pipeline management',
    'sales deal tracking',
    'team pipeline collaboration',
    'business card maker',
    'virtual contact management',
    'digital networking tool',
    'online contact sharing',
    'business card scanner alternative',
    'QR code generator for business',
    'contact card creator',
    
    // Location-based
    'digital business card India',
    'smart visiting card',
    'online visiting card maker',
    'contactless business card',
    
    // Use-case specific
    'networking tool for professionals',
    'digital card for freelancers',
    'business card for startups',
    'corporate digital cards'
  ],
  author: 'Piper Card Team',
  twitterHandle: '@PiperCard',
  defaultImage: 'https://pipercard.com/piper-card-og.png',
  themeColor: '#d97706',
  locale: 'en_US',
  type: 'website',
  organization: {
    name: 'Piper Card',
    url: 'https://pipercard.com',
    logo: 'https://pipercard.com/logo512.png',
    sameAs: [
      'https://twitter.com/PiperCard',
      'https://linkedin.com/company/pipercard',
      'https://facebook.com/PiperCard'
    ]
  }
};

// Generate dynamic meta description from card data
export const generateCardDescription = (card) => {
  const parts = [];
  
  if (card.name) parts.push(card.name);
  if (card.title) parts.push(card.title);
  if (card.company) parts.push(card.company);
  
  const description = parts.length > 0 
    ? `${parts.join(' - ')}. Connect with me via digital business card on Piper Card.`
    : 'View my digital business card on Piper Card. Instant contact sharing with QR code.';
  
  if (card.bio && parts.length > 0) {
    return `${description} ${card.bio.substring(0, 100)}...`;
  }
  
  return description;
};

// Generate card title
export const generateCardTitle = (card) => {
  const parts = [];
  
  if (card.name) parts.push(card.name);
  if (card.title) parts.push(card.title);
  
  return parts.length > 0 
    ? `${parts.join(' - ')} - Digital Business Card`
    : 'Digital Business Card';
};

// SEO-friendly URL generator
export const generateCanonicalUrl = (path) => {
  const baseUrl = SEO_CONFIG.siteUrl;
  return `${baseUrl}${path}`;
};

// Keywords generator for specific pages
export const getPageKeywords = (page, customKeywords = []) => {
  const baseKeywords = SEO_CONFIG.keywords.slice(0, 10); // First 10 base keywords
  return [...baseKeywords, ...customKeywords].join(', ');
};

// Generate Organization Schema
export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SEO_CONFIG.siteName,
    "alternateName": "PiperCard",
    "url": SEO_CONFIG.siteUrl,
    "logo": SEO_CONFIG.organization.logo,
    "description": SEO_CONFIG.defaultDescription,
    "sameAs": SEO_CONFIG.organization.sameAs,
    "knowsAbout": ["Digital Business Cards", "CRM Software", "Sales Pipeline Management", "Team Collaboration", "Contact Management"],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "availableLanguage": ["English"]
    }
  };
};

// Generate Person/BusinessCard Schema for Bio Pages
export const generatePersonSchema = (card) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": card.name,
    "url": `${SEO_CONFIG.siteUrl}/bio/${card.username}`,
    "image": card.profileImage || card.avatar,
  };

  if (card.title) schema.jobTitle = card.title;
  if (card.company) schema.worksFor = {
    "@type": "Organization",
    "name": card.company
  };
  if (card.email) schema.email = card.email;
  if (card.phone) schema.telephone = card.phone;
  if (card.location) schema.address = card.location;
  if (card.bio) schema.description = card.bio;
  
  // Social Media Links
  const sameAs = [];
  if (card.linkedin) sameAs.push(card.linkedin);
  if (card.twitter) sameAs.push(card.twitter);
  if (card.github) sameAs.push(card.github);
  if (card.facebook) sameAs.push(card.facebook);
  if (card.instagram) sameAs.push(card.instagram);
  if (card.youtube) sameAs.push(card.youtube);
  if (sameAs.length > 0) schema.sameAs = sameAs;

  return schema;
};

// Generate WebPage Schema
export const generateWebPageSchema = (title, description, url) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": url,
    "publisher": {
      "@type": "Organization",
      "name": SEO_CONFIG.siteName,
      "logo": {
        "@type": "ImageObject",
        "url": SEO_CONFIG.organization.logo
      }
    }
  };
};

// Generate BreadcrumbList Schema
export const generateBreadcrumbSchema = (breadcrumbs) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${SEO_CONFIG.siteUrl}${crumb.path}`
    }))
  };
};

// Generate FAQ Schema
export const generateFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Generate HowTo Schema for card creation
export const generateHowToSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Create a Digital Business Card with QR Code",
    "description": "Step-by-step guide to creating your digital business card on Piper Card",
    "image": SEO_CONFIG.defaultImage,
    "totalTime": "PT5M",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Sign Up",
        "text": "Create a free account on Piper Card",
        "url": `${SEO_CONFIG.siteUrl}/signup`
      },
      {
        "@type": "HowToStep",
        "name": "Enter Your Information",
        "text": "Add your contact details, social media links, and professional information",
        "url": `${SEO_CONFIG.siteUrl}/create`
      },
      {
        "@type": "HowToStep",
        "name": "Customize Design",
        "text": "Choose colors, add your photo, and customize your card design",
        "url": `${SEO_CONFIG.siteUrl}/create`
      },
      {
        "@type": "HowToStep",
        "name": "Generate QR Code",
        "text": "Your unique QR code is automatically generated",
        "url": `${SEO_CONFIG.siteUrl}/create`
      },
      {
        "@type": "HowToStep",
        "name": "Share Your Card",
        "text": "Share your digital business card via QR code, link, or social media",
        "url": `${SEO_CONFIG.siteUrl}/create`
      }
    ]
  };
};
